/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import { HttpClient, HttpEvent } from '@angular/common/http';
import {Observable} from 'rxjs';
import {FileSummaryModel} from './contracts/file-summary.model';
import {environment} from '../../../environments/environment';
import {FileManagerConfigurationModel} from './contracts/file-manager-configuration.model';
import {Cacheable} from '../../decorators/cacheable.decorator';

@Injectable({
    providedIn: 'root'
})
export class FileManagerApi {

    private fileManagerBaseUrl = `${environment.fileManagerBaseUrl}/api/folders`;
    private fileManagerConfigurationsBaseUrl = `${environment.fileManagerBaseUrl}/api/config`;

    constructor(private http: HttpClient) {
    }

    /**
     * Get file manager configurations.
     *
     * @return file manager configurations.
     */
    @Cacheable()
    getFileManagerConfigurations(): Observable<FileManagerConfigurationModel> {
        return this.http.get<FileManagerConfigurationModel>(`${this.fileManagerConfigurationsBaseUrl}`);
    }

    /**
     * Create a new directory.
     *
     * @return the response
     */
    createNewDirectory(): Observable<string> {
        return this.http.post(`${this.fileManagerBaseUrl}`,
                undefined,
                {responseType: 'text'});
    }

    /**
     * Clones an existing directory.
     *
     * @param sourceFolder the folder to be cloned
     * @return the response
     */
    cloneDirectory(sourceFolder: string): Observable<string> {
        return this.http.post(`${this.fileManagerBaseUrl}/${sourceFolder}/clone`,
                undefined,
                {responseType: 'text'});
    }

    /**
     * Delete the directory and all the contained files.
     *
     * @param folder      the directory folder to delete
     */
    deleteDirectory(folder: string): Observable<void> {
        return this.http.delete<void>(
                `${this.fileManagerBaseUrl}/${folder}`);
    }

    /**
     * Retrieve the directory files summary.
     *
     * @param folder      the directory folder
     * @return the directory files summaries
     */
    getDirectorySummary(folder: string): Observable<FileSummaryModel[]> {
        return this.http.get<FileSummaryModel[]>(
                `${this.fileManagerBaseUrl}/${folder}/files`);
    }

    /**
     * Upload a file.
     *
     * @param folder              the file folder
     * @param formData            the file content
     * @return the uploaded file name
     */
    uploadFile(folder: string, formData: FormData): Observable<HttpEvent<any>> {
        return this.http.post<FileSummaryModel>(
                `${this.fileManagerBaseUrl}/${folder}/files`,
                formData,
                {
                    reportProgress: true, observe: 'events'
                }
        );
    }

    /**
     * Download a file.
     *
     * @param folder      the file folder
     * @param fileName    the file name
     * @return the file content
     */
    downloadFile(folder: string, fileName: string): Observable<Blob> {
        return this.http.get(
                `${this.fileManagerBaseUrl}/${folder}/files/${fileName}/download`,
                {responseType: 'blob'});
    }

    /**
     * Preview a file.
     *
     * @param folder      the file folder
     * @param fileName    the file name
     * @return the file content
     */
    previewFile(folder: string, fileName: string): Observable<any> {
        return this.http.get<any>(
                `${this.fileManagerBaseUrl}/${folder}/files/${fileName}/preview`);
    }

    /**
     * Delete a file.
     *
     * @param folder      the directory folder to delete
     * @param fileName    the file name
     */
    deleteFile(folder: string, fileName: string): Observable<void> {
        return this.http.delete<void>(
                `${this.fileManagerBaseUrl}/${folder}/files/${fileName}`);
    }
}
